import React from "react";
import { useToast } from "../components/ResultsPrimitives.jsx";
import {
  closeExportDocumentWindow,
  downloadTruckSheetPdfExport,
  getTruckSheetPdfExportHistory,
  getTruckSheetPdfExportStatus,
  isExportApiError,
  launchTruckSheetPdfExport,
  markExportDocumentWindowFailed,
  openExportDocumentWindow,
} from "../lib/exportApi.js";
import { appendDispatchDateToUrl } from "../utils/dispatchDateUrls.js";
import { clearAppStatusNoticeBySource, dispatchAppStatusNotice } from "../utils/appStatusNotice.js";

const DEFAULT_EXPORT_STATE = {
  downloadUrl: "",
  errorCode: "",
  exportId: "",
  expiresAt: null,
  fingerprint: "",
  finishedAt: null,
  message: "",
  pollIntervalMs: 2000,
  resultUrl: "",
  rowCount: 0,
  startedAt: null,
  status: "idle",
};

function buildFitOnePageWarningToast(warning) {
  return { duration: 12000, message: warning, title: "Plus d'une page", type: "warning" };
}

const CHANNEL_CONFIG = {
  fleet: {
    blockedTitle: "Export horaires chauffeur bloqué",
    browserFallbackFileName: "grain_truck_sheets.pdf",
    downloadReadyRoute: "/results?downloadTruckSheetPdf=fleet",
    exportMode: "combined",
    failedTitle: "Export horaires chauffeur interrompu",
    label: "Horaires chauffeur",
    loadingMessage: "Préparation du PDF groupé des horaires chauffeur en arrière-plan. Vous pouvez continuer à naviguer.",
    loadingTitle: "Export horaires chauffeur...",
    noContentTitle: "Aucun horaire chauffeur exportable",
    noticeSource: "truck_sheet_pdf_export_fleet",
    readyTitle: "Export horaires chauffeur prêt",
    syncSuccessMessage: "L'export des horaires chauffeur a été téléchargé immédiatement.",
  },
  selected: {
    blockedTitle: "Export horaire chauffeur bloqué",
    browserFallbackFileName: "grain_truck_sheet.pdf",
    downloadReadyRoute: "/results?downloadTruckSheetPdf=selected",
    exportMode: "single",
    failedTitle: "Export horaire chauffeur interrompu",
    label: "Horaire chauffeur",
    loadingMessage: "Préparation de l'horaire chauffeur en arrière-plan. Vous pouvez continuer à naviguer.",
    loadingTitle: "Export horaire chauffeur...",
    noContentTitle: "Aucun horaire chauffeur exportable",
    noticeSource: "truck_sheet_pdf_export_selected",
    readyTitle: "Horaire chauffeur prêt",
    syncSuccessMessage: "L'horaire chauffeur a été téléchargé immédiatement.",
  },
};

const PDF_BROWSER_DESTINATION_LABEL = "Téléchargements du navigateur";
const TruckSheetPdfExportTrackerContext = React.createContext(null);

function buildDefaultExportState() {
  return { ...DEFAULT_EXPORT_STATE };
}

function buildDefaultChannelStates() {
  return {
    fleet: buildDefaultExportState(),
    selected: buildDefaultExportState(),
  };
}

function buildDefaultChannelRuntimeRefs() {
  return {
    fleet: {
      autoDownloadOnReady: false,
      autoOpenFingerprint: "",
      documentWindow: null,
      shouldAutoOpenInDocumentWindow: false,
      historyAbortController: null,
      hydratedFingerprint: "",
      loadingToastId: null,
      pollAbortController: null,
    },
    selected: {
      autoDownloadOnReady: false,
      autoOpenFingerprint: "",
      documentWindow: null,
      shouldAutoOpenInDocumentWindow: false,
      historyAbortController: null,
      hydratedFingerprint: "",
      loadingToastId: null,
      pollAbortController: null,
    },
  };
}

function normalizeChannel(channel) {
  return channel === "selected" ? "selected" : "fleet";
}

function getChannelConfig(channel) {
  return CHANNEL_CONFIG[normalizeChannel(channel)];
}

function normalizeFingerprint(value) {
  return typeof value === "string" ? value.trim() : "";
}

function resolveChannelReadyRoute(channel, resultUrl = "") {
  const normalizedResultUrl = typeof resultUrl === "string" ? resultUrl.trim() : "";
  return appendDispatchDateToUrl(normalizedResultUrl || getChannelConfig(channel).downloadReadyRoute);
}

function resolveFiniteNumber(value, fallbackValue) {
  if (value == null || value === "") {
    return fallbackValue;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallbackValue;
}

function resolveOptionalTimestamp(value) {
  return typeof value === "string" && value.trim() ? value : null;
}

function buildPendingExportState({
  exportId,
  fingerprint = "",
  payload = null,
  pollIntervalFallback = 2000,
  resultUrl = "",
  rowCountFallback = 0,
  startedAtFallback = null,
} = {}) {
  return {
    ...buildDefaultExportState(),
    exportId,
    fingerprint: normalizeFingerprint(fingerprint || payload?.fingerprint),
    message: typeof payload?.message === "string" ? payload.message : "Export en préparation.",
    pollIntervalMs: resolveFiniteNumber(
      payload?.tracking?.recommended_poll_interval_ms,
      resolveFiniteNumber(payload?.recommended_poll_interval_ms, pollIntervalFallback),
    ),
    resultUrl,
    rowCount: resolveFiniteNumber(payload?.row_count, rowCountFallback),
    startedAt: resolveOptionalTimestamp(payload?.started_at) || startedAtFallback,
    status: "pending_async",
  };
}

function buildReadyExportState({
  exportId,
  fingerprint = "",
  payload = null,
  resultUrl = "",
  rowCountFallback = 0,
  startedAtFallback = null,
} = {}) {
  return {
    ...buildDefaultExportState(),
    downloadUrl: typeof payload?.download_url === "string" ? payload.download_url : "",
    exportId,
    expiresAt: resolveOptionalTimestamp(payload?.expires_at),
    fingerprint: normalizeFingerprint(fingerprint || payload?.fingerprint),
    finishedAt: resolveOptionalTimestamp(payload?.finished_at),
    message: typeof payload?.message === "string" ? payload.message : "Export prêt au téléchargement.",
    resultUrl,
    rowCount: resolveFiniteNumber(payload?.row_count, rowCountFallback),
    startedAt: resolveOptionalTimestamp(payload?.started_at) || startedAtFallback,
    status: "ready_async",
  };
}

function formatExportClockTime(value) {
  if (typeof value !== "string" || !value.trim()) {
    return "";
  }

  const resolvedDate = new Date(value);
  if (Number.isNaN(resolvedDate.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("fr-CA", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(resolvedDate);
}

function buildCompletedNotice(channel, exportState) {
  const config = getChannelConfig(channel);
  const finishedAtLabel = formatExportClockTime(exportState.finishedAt);
  return {
    source: config.noticeSource,
    status: "completed",
    eyebrow: "Horaire chauffeur",
    title: config.readyTitle,
    message: finishedAtLabel
      ? `Le document PDF est prêt depuis ${finishedAtLabel}.`
      : "Le document PDF est prêt au téléchargement.",
    detailItems: [
      {
        key: "row-count",
        label: "Étapes exportées",
        value: String(exportState.rowCount || 0),
      },
      ...(exportState.expiresAt
        ? [{
            key: "expires-at",
            label: "Disponible jusqu'à",
            value: exportState.expiresAt,
          }]
        : []),
    ],
    finishedAt: exportState.finishedAt,
    resultActionLabel: "Ouvrir le PDF",
    resultUrl: resolveChannelReadyRoute(channel, exportState?.resultUrl),
    statusLabel: "Téléchargement prêt",
  };
}

function buildRunningNotice(channel, {
  message = "Préparation du PDF. Le téléchargement sera ensuite géré par votre navigateur.",
  rowCount = 0,
  startedAt = null,
} = {}) {
  const config = getChannelConfig(channel);
  return {
    source: config.noticeSource,
    status: "running",
    eyebrow: "Horaire chauffeur",
    title: `${config.label} en cours`,
    message,
    detailItems: [
      ...(rowCount > 0
        ? [{
            key: "row-count",
            label: "Étapes à exporter",
            value: String(rowCount),
          }]
        : []),
      {
        key: "download-destination",
        label: "Emplacement",
        value: PDF_BROWSER_DESTINATION_LABEL,
      },
    ],
    startedAt,
    statusLabel: "Préparation",
  };
}

function buildDownloadedNotice(channel, {
  destinationLabel = PDF_BROWSER_DESTINATION_LABEL,
  fileName,
  finishedAt = null,
  openedInWindow = false,
  rowCount = 0,
} = {}) {
  const config = getChannelConfig(channel);
  const title = openedInWindow ? `${config.label} ouvert` : `${config.label} téléchargé`;
  const message = openedInWindow
    ? `Le document ${fileName || config.browserFallbackFileName} a été ouvert dans une nouvelle fenêtre du navigateur.`
    : `Le téléchargement de ${fileName || config.browserFallbackFileName} a été lancé. Vérifiez l'icône de téléchargements du navigateur ou le dossier Téléchargements.`;
  return {
    source: config.noticeSource,
    status: "completed",
    eyebrow: "Horaire chauffeur",
    title,
    message,
    detailItems: [
      {
        key: "file-name",
        label: "Fichier",
        value: fileName || config.browserFallbackFileName,
      },
      ...(rowCount > 0
        ? [{
            key: "row-count",
            label: "Étapes exportées",
            value: String(rowCount),
          }]
        : []),
      {
        key: "download-destination",
        label: "Emplacement",
        value: destinationLabel,
      },
    ],
    finishedAt,
    statusLabel: openedInWindow ? "Aperçu ouvert" : "Téléchargement lancé",
  };
}

function buildFailedNotice(channel, {
  detailItems = [],
  message,
  title,
}) {
  const config = getChannelConfig(channel);
  return {
    source: config.noticeSource,
    status: "failed",
    eyebrow: "Horaire chauffeur",
    title,
    message,
    detailItems,
    statusLabel: "Action requise",
  };
}

export function TruckSheetPdfExportProvider({ children }) {
  const { dismissToast, showToast, updateToast } = useToast();
  const [exportStates, setExportStates] = React.useState(buildDefaultChannelStates);
  const exportStatesRef = React.useRef(exportStates);
  const runtimeRefs = React.useRef(buildDefaultChannelRuntimeRefs());

  React.useEffect(() => {
    exportStatesRef.current = exportStates;
  }, [exportStates]);

  React.useEffect(() => () => {
    Object.values(runtimeRefs.current).forEach((entry) => {
      entry?.pollAbortController?.abort();
      entry?.historyAbortController?.abort();
    });
  }, []);

  const setChannelState = React.useCallback((channel, nextState) => {
    const normalizedChannel = normalizeChannel(channel);
    setExportStates((currentStates) => ({
      ...currentStates,
      [normalizedChannel]: nextState,
    }));
  }, []);

  const clearChannelNotice = React.useCallback((channel) => {
    clearAppStatusNoticeBySource(getChannelConfig(channel).noticeSource);
  }, []);

  const publishRunningNotice = React.useCallback((channel, detail = {}) => {
    dispatchAppStatusNotice(buildRunningNotice(channel, detail));
  }, []);

  const clearPendingDocumentWindow = React.useCallback((channel, { close = false } = {}) => {
    const normalizedChannel = normalizeChannel(channel);
    const channelRefs = runtimeRefs.current[normalizedChannel];
    if (close && channelRefs.documentWindow) {
      closeExportDocumentWindow(channelRefs.documentWindow);
    }

    channelRefs.autoDownloadOnReady = false;
    channelRefs.autoOpenFingerprint = "";
    channelRefs.documentWindow = null;
    channelRefs.shouldAutoOpenInDocumentWindow = false;
  }, []);

  const preparePdfDocumentWindow = React.useCallback((channel, fingerprint, openDocumentWindow = false) => {
    const normalizedChannel = normalizeChannel(channel);
    const config = getChannelConfig(normalizedChannel);
    const channelRefs = runtimeRefs.current[normalizedChannel];
    clearPendingDocumentWindow(normalizedChannel, { close: true });
    if (!openDocumentWindow) {
      return null;
    }

    channelRefs.autoOpenFingerprint = normalizeFingerprint(fingerprint);
    channelRefs.shouldAutoOpenInDocumentWindow = true;
    channelRefs.documentWindow = openExportDocumentWindow({
      detail: "Le PDF s’affichera automatiquement dès qu’il sera disponible.",
      eyebrow: "Horaire chauffeur",
      message: `Préparation en cours pour ${config.label.toLowerCase()}.`,
      preferredHeight: 900,
      preferredWidth: 1180,
      title: `Préparation — ${config.label}`,
      windowName: `grain-${normalizedChannel}-truck-sheet-pdf-${Date.now()}`,
    });
    return channelRefs.documentWindow;
  }, [clearPendingDocumentWindow]);

  const openLoadingToast = React.useCallback((channel, message) => {
    const normalizedChannel = normalizeChannel(channel);
    const config = getChannelConfig(normalizedChannel);
    const channelRefs = runtimeRefs.current[normalizedChannel];
    if (channelRefs.loadingToastId) {
      updateToast(channelRefs.loadingToastId, {
        message,
        title: config.loadingTitle,
        type: "loading",
      });
      return;
    }

    channelRefs.loadingToastId = showToast({
      message,
      title: config.loadingTitle,
      type: "loading",
    });
  }, [showToast, updateToast]);

  const closeLoadingToastWithResult = React.useCallback((channel, toast) => {
    const channelRefs = runtimeRefs.current[normalizeChannel(channel)];
    if (channelRefs.loadingToastId) {
      updateToast(channelRefs.loadingToastId, toast);
      channelRefs.loadingToastId = null;
      return;
    }

    showToast(toast);
  }, [showToast, updateToast]);

  const dismissLoadingToast = React.useCallback((channel) => {
    const channelRefs = runtimeRefs.current[normalizeChannel(channel)];
    if (!channelRefs.loadingToastId) {
      return;
    }

    dismissToast(channelRefs.loadingToastId);
    channelRefs.loadingToastId = null;
  }, [dismissToast]);

  const applyFailureState = React.useCallback((channel, nextState, notice) => {
    closeLoadingToastWithResult(channel, {
      duration: 8000,
      title: notice.title,
      message: notice.message,
      type: "error",
    });
    dispatchAppStatusNotice(notice);
    setChannelState(channel, nextState);
  }, [closeLoadingToastWithResult, setChannelState]);

  const handleExportApiError = React.useCallback((channel, error, {
    fallbackMessage = "L'export PDF n'a pas pu aboutir.",
    fingerprint = "",
  } = {}) => {
    const config = getChannelConfig(channel);
    const channelRefs = runtimeRefs.current[normalizeChannel(channel)];
    const inlineMessage = !isExportApiError(error) && error && typeof error === "object" && typeof error.message === "string"
      ? error.message.trim()
      : "";
    const message = isExportApiError(error) && typeof error.message === "string" && error.message.trim()
      ? error.message.trim()
      : inlineMessage || fallbackMessage;
    const inlineCode = !isExportApiError(error) && error && typeof error === "object" && typeof error.code === "string"
      ? error.code.trim()
      : "";
    const code = isExportApiError(error) && typeof error.code === "string"
      ? error.code.trim()
      : inlineCode || "export_generation_failed";
    const applyFailure = (nextState, notice) => {
      markExportDocumentWindowFailed(channelRefs.documentWindow, {
        eyebrow: "Horaire chauffeur",
        message: notice.message,
        title: notice.title,
      });
      clearPendingDocumentWindow(channel);
      applyFailureState(channel, nextState, notice);
    };

    if (code === "latest_optimization_stale") {
      applyFailure(
        {
          ...buildDefaultExportState(),
          errorCode: code,
          fingerprint,
          message,
          status: "blocked_stale",
        },
        buildFailedNotice(channel, {
          message,
          title: config.blockedTitle,
        }),
      );
      return;
    }

    if (code === "permission_denied") {
      applyFailure(
        {
          ...buildDefaultExportState(),
          errorCode: code,
          fingerprint,
          message,
          status: "permission_denied",
        },
        buildFailedNotice(channel, {
          message,
          title: `${config.label} refusé`,
        }),
      );
      return;
    }

    if (code === "latest_optimization_not_found") {
      applyFailure(
        {
          ...buildDefaultExportState(),
          errorCode: code,
          fingerprint,
          message,
          status: "no_content",
        },
        buildFailedNotice(channel, {
          message,
          title: config.noContentTitle,
        }),
      );
      return;
    }

    if (code === "truck_sheet_export_invalid_request") {
      applyFailure(
        {
          ...buildDefaultExportState(),
          errorCode: code,
          fingerprint,
          message,
          status: "invalid_request",
        },
        buildFailedNotice(channel, {
          message,
          title: "Sélection PDF invalide",
        }),
      );
      return;
    }

    if (code === "truck_sheet_pdf_runtime_dependency_missing") {
      applyFailure(
        {
          ...buildDefaultExportState(),
          errorCode: code,
          fingerprint,
          message,
          status: "runtime_dependency_missing",
        },
        buildFailedNotice(channel, {
          message,
          title: `${config.label} indisponible pour le moment`,
        }),
      );
      return;
    }

    if (code === "export_expired") {
      applyFailure(
        {
          ...buildDefaultExportState(),
          errorCode: code,
          fingerprint,
          message,
          status: "expired",
        },
        buildFailedNotice(channel, {
          message,
          title: `${config.label} expiré`,
        }),
      );
      return;
    }

    applyFailure(
      {
        ...buildDefaultExportState(),
        errorCode: code,
        fingerprint,
        message,
        status: "failed",
      },
      buildFailedNotice(channel, {
        message,
        title: config.failedTitle,
      }),
    );
  }, [applyFailureState, clearPendingDocumentWindow]);

  const performPdfDownload = React.useCallback(async (channel, {
    documentWindow = null,
    exportId = "",
    fingerprint = "",
    openDocumentWindow = false,
    rowCount = null,
  } = {}) => {
    const normalizedChannel = normalizeChannel(channel);
    const config = getChannelConfig(normalizedChannel);
    const currentState = exportStatesRef.current[normalizedChannel];
    const resolvedExportId = typeof exportId === "string" && exportId.trim()
      ? exportId.trim()
      : currentState.exportId;
    if (!resolvedExportId || (!exportId && currentState.status !== "ready_async")) {
      return currentState;
    }

    const resolvedFingerprint = normalizeFingerprint(fingerprint) || currentState.fingerprint;
    const resolvedRowCount = Number.isFinite(Number(rowCount)) ? Number(rowCount) : currentState.rowCount;
    const resolvedDocumentWindow = documentWindow || (openDocumentWindow
      ? preparePdfDocumentWindow(normalizedChannel, resolvedFingerprint, true)
      : null);

    try {
      const download = await downloadTruckSheetPdfExport(resolvedExportId, {
        documentWindow: resolvedDocumentWindow,
      });
      clearPendingDocumentWindow(normalizedChannel);
      dispatchAppStatusNotice(buildDownloadedNotice(normalizedChannel, {
        destinationLabel: download.destinationLabel || PDF_BROWSER_DESTINATION_LABEL,
        fileName: download.fileName || config.browserFallbackFileName,
        finishedAt: new Date().toISOString(),
        openedInWindow: download.openedInWindow === true,
        rowCount: resolvedRowCount,
      }));
      showToast({
        duration: 5000,
        title: download.openedInWindow === true ? "PDF ouvert" : "Téléchargement lancé",
        message: download.openedInWindow === true
          ? "Le document PDF a été ouvert dans une nouvelle fenêtre."
          : "Le fichier PDF est en cours de téléchargement.",
        type: "success",
      });
      return exportStatesRef.current[normalizedChannel];
    } catch (error) {
      handleExportApiError(normalizedChannel, error, {
        fingerprint: resolvedFingerprint,
      });
      return exportStatesRef.current[normalizedChannel];
    }
  }, [clearPendingDocumentWindow, handleExportApiError, preparePdfDocumentWindow, showToast]);

  const pollExportStatus = React.useCallback(async (channel, exportId) => {
    const normalizedChannel = normalizeChannel(channel);
    const channelRefs = runtimeRefs.current[normalizedChannel];
    channelRefs.pollAbortController?.abort();
    const abortController = new AbortController();
    channelRefs.pollAbortController = abortController;

    try {
      const payload = await getTruckSheetPdfExportStatus(exportId, {
        signal: abortController.signal,
      });
      const currentState = exportStatesRef.current[normalizedChannel];
      if (payload?.status === "pending") {
        setChannelState(normalizedChannel, buildPendingExportState({
          exportId,
          fingerprint: normalizeFingerprint(payload?.fingerprint) || currentState.fingerprint,
          payload,
          pollIntervalFallback: currentState.pollIntervalMs || 2000,
          resultUrl: currentState.resultUrl,
          rowCountFallback: currentState.rowCount,
          startedAtFallback: currentState.startedAt,
        }));
        return;
      }

      if (payload?.status === "completed") {
        if (payload.warning) {
          showToast(buildFitOnePageWarningToast(payload.warning));
        }
        const nextState = buildReadyExportState({
          exportId,
          fingerprint: normalizeFingerprint(payload?.fingerprint) || currentState.fingerprint,
          payload,
          resultUrl: currentState.resultUrl,
          rowCountFallback: currentState.rowCount,
          startedAtFallback: currentState.startedAt,
        });
        setChannelState(normalizedChannel, nextState);
        if (channelRefs.shouldAutoOpenInDocumentWindow && channelRefs.documentWindow) {
          const pendingDocumentWindow = channelRefs.documentWindow;
          channelRefs.shouldAutoOpenInDocumentWindow = false;
          dismissLoadingToast(normalizedChannel);
          clearPendingDocumentWindow(normalizedChannel);
          await performPdfDownload(normalizedChannel, {
            documentWindow: pendingDocumentWindow,
            exportId,
            fingerprint: nextState.fingerprint,
            rowCount: nextState.rowCount,
          });
          setChannelState(normalizedChannel, buildDefaultExportState());
          return;
        }

        if (channelRefs.autoDownloadOnReady) {
          channelRefs.autoDownloadOnReady = false;
          dismissLoadingToast(normalizedChannel);
          clearPendingDocumentWindow(normalizedChannel);
          await performPdfDownload(normalizedChannel, {
            exportId,
            fingerprint: nextState.fingerprint,
            openDocumentWindow: false,
            rowCount: nextState.rowCount,
          });
          setChannelState(normalizedChannel, buildDefaultExportState());
          return;
        }

        closeLoadingToastWithResult(normalizedChannel, {
          duration: 7000,
          title: "Export prêt",
          message: nextState.message,
          type: "success",
        });
        dispatchAppStatusNotice(buildCompletedNotice(normalizedChannel, nextState));
        return;
      }

      if (payload?.status === "failed") {
        handleExportApiError(normalizedChannel, new Error(typeof payload?.error === "string" ? payload.error : typeof payload?.message === "string" ? payload.message : "L'export a échoué."), {
          fallbackMessage: typeof payload?.message === "string" ? payload.message : "L'export a échoué.",
          fingerprint: currentState.fingerprint,
        });
        return;
      }

      if (payload?.status === "expired") {
        handleExportApiError(normalizedChannel, { code: "export_expired", message: typeof payload?.message === "string" ? payload.message : "L'export n'est plus disponible." }, {
          fingerprint: currentState.fingerprint,
        });
        return;
      }

      handleExportApiError(normalizedChannel, new Error("Le statut d'export reçu est invalide."), {
        fingerprint: currentState.fingerprint,
      });
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }

      handleExportApiError(normalizedChannel, error, {
        fallbackMessage: "Le suivi de l'export PDF n'est plus disponible.",
        fingerprint: exportStatesRef.current[normalizedChannel].fingerprint,
      });
    }
  }, [clearPendingDocumentWindow, closeLoadingToastWithResult, dismissLoadingToast, handleExportApiError, performPdfDownload, setChannelState, showToast]);

  const restoreFromHistory = React.useCallback(async (channel, fingerprint, {
    resultUrl = "",
  } = {}) => {
    const normalizedChannel = normalizeChannel(channel);
    const normalizedFingerprint = normalizeFingerprint(fingerprint);
    const resolvedResultUrl = resolveChannelReadyRoute(normalizedChannel, resultUrl);
    if (!normalizedFingerprint) {
      return;
    }

    const currentState = exportStatesRef.current[normalizedChannel];
    if (currentState.status === "launching" || currentState.status === "pending_async") {
      return;
    }

    if (currentState.fingerprint === normalizedFingerprint && currentState.exportId) {
      return;
    }

    const channelRefs = runtimeRefs.current[normalizedChannel];
    if (channelRefs.hydratedFingerprint === normalizedFingerprint) {
      return;
    }

    channelRefs.hydratedFingerprint = normalizedFingerprint;
    channelRefs.historyAbortController?.abort();
    const abortController = new AbortController();
    channelRefs.historyAbortController = abortController;

    try {
      const payload = await getTruckSheetPdfExportHistory({ signal: abortController.signal });
      if (abortController.signal.aborted) {
        return;
      }

      const matchingItem = Array.isArray(payload?.items)
        ? payload.items.find((item) => normalizeFingerprint(item?.fingerprint) === normalizedFingerprint)
        : null;
      const exportId = typeof matchingItem?.export_id === "string" ? matchingItem.export_id : "";
      if (!matchingItem || !exportId) {
        return;
      }

      if (matchingItem.status === "completed") {
        const nextState = buildReadyExportState({
          exportId,
          fingerprint: normalizedFingerprint,
          payload: matchingItem,
          resultUrl: resolvedResultUrl,
        });
        setChannelState(normalizedChannel, nextState);
        dispatchAppStatusNotice(buildCompletedNotice(normalizedChannel, nextState));
        return;
      }

      if (matchingItem.status === "pending") {
        const nextState = buildPendingExportState({
          exportId,
          fingerprint: normalizedFingerprint,
          payload: matchingItem,
          resultUrl: resolvedResultUrl,
        });
        setChannelState(normalizedChannel, nextState);
        publishRunningNotice(normalizedChannel, {
          message: nextState.message,
          rowCount: nextState.rowCount,
          startedAt: nextState.startedAt,
        });
      }
    } catch {
      if (abortController.signal.aborted) {
        return;
      }
    }
  }, [publishRunningNotice, setChannelState]);

  React.useEffect(() => {
    const exportState = exportStates.fleet;
    if (exportState.status !== "pending_async" || !exportState.exportId || typeof window === "undefined") {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      void pollExportStatus("fleet", exportState.exportId);
    }, exportState.pollIntervalMs || 2000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [exportStates.fleet, pollExportStatus]);

  React.useEffect(() => {
    const exportState = exportStates.selected;
    if (exportState.status !== "pending_async" || !exportState.exportId || typeof window === "undefined") {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      void pollExportStatus("selected", exportState.exportId);
    }, exportState.pollIntervalMs || 2000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [exportStates.selected, pollExportStatus]);

  const launchTrackedExport = React.useCallback(async ({
    blocked = false,
    blockedReason = "",
    channel = "fleet",
    dispatchPeriod = "",
    fingerprint = "",
    openDocumentWindow = false,
    resultUrl = "",
    fitOnePage = false,
    notes = "",
    onCallDispatcher = "",
    orientation = "portrait",
    showCompartments = true,
    showMap = true,
    truckIds = [],
  } = {}) => {
    const normalizedChannel = normalizeChannel(channel);
    const config = getChannelConfig(normalizedChannel);
    const currentState = exportStatesRef.current[normalizedChannel];
    const normalizedFingerprint = normalizeFingerprint(fingerprint);
    const resolvedResultUrl = resolveChannelReadyRoute(normalizedChannel, resultUrl);

    if (currentState.status === "launching" || currentState.status === "pending_async") {
      return currentState;
    }

    if (blocked) {
      handleExportApiError(normalizedChannel, {
        code: "latest_optimization_stale",
        message: blockedReason || "Dispatch à revalider : relancez « Optimiser » avant d'imprimer l'horaire.",
      }, {
        fingerprint: normalizedFingerprint,
      });
      return currentState;
    }

    clearChannelNotice(normalizedChannel);
    runtimeRefs.current[normalizedChannel].historyAbortController?.abort();
    runtimeRefs.current[normalizedChannel].hydratedFingerprint = normalizedFingerprint;
    const documentWindow = preparePdfDocumentWindow(normalizedChannel, normalizedFingerprint, openDocumentWindow);
    runtimeRefs.current[normalizedChannel].autoDownloadOnReady = openDocumentWindow === false;
    openLoadingToast(normalizedChannel, config.loadingMessage);
    const startedAt = new Date().toISOString();
    setChannelState(normalizedChannel, {
      ...buildDefaultExportState(),
      fingerprint: normalizedFingerprint,
      message: config.loadingMessage,
      resultUrl: resolvedResultUrl,
      startedAt,
      status: "launching",
    });
    publishRunningNotice(normalizedChannel, { startedAt });

    try {
      const result = await launchTruckSheetPdfExport({
        dispatchPeriod,
        documentWindow,
        exportMode: config.exportMode,
        fitOnePage,
        notes,
        onCallDispatcher,
        orientation,
        showCompartments,
        showMap,
        truckIds,
      });
      if (result.mode === "sync") {
        dismissLoadingToast(normalizedChannel);
        clearPendingDocumentWindow(normalizedChannel);
        dispatchAppStatusNotice(buildDownloadedNotice(normalizedChannel, {
          destinationLabel: result.download?.destinationLabel || PDF_BROWSER_DESTINATION_LABEL,
          fileName: result.download?.fileName || config.browserFallbackFileName,
          finishedAt: new Date().toISOString(),
          openedInWindow: result.download?.openedInWindow === true,
        }));
        showToast({
          duration: 6000,
          title: result.download?.openedInWindow === true ? "PDF ouvert" : "Téléchargement lancé",
          message: result.download?.openedInWindow === true
            ? "Le document PDF a été ouvert dans une nouvelle fenêtre."
            : config.syncSuccessMessage,
          type: "success",
        });
        if (result.warning) {
          showToast(buildFitOnePageWarningToast(result.warning));
        }
        const idleState = buildDefaultExportState();
        setChannelState(normalizedChannel, idleState);
        return idleState;
      }

      const payload = result.payload || {};
      const nextState = buildPendingExportState({
        exportId: typeof payload?.export_id === "string" ? payload.export_id : "",
        fingerprint: normalizedFingerprint,
        payload,
        resultUrl: resolvedResultUrl,
        startedAtFallback: startedAt,
      });
      setChannelState(normalizedChannel, nextState);
      publishRunningNotice(normalizedChannel, {
        message: nextState.message || config.loadingMessage,
        rowCount: nextState.rowCount,
        startedAt: nextState.startedAt,
      });
      return nextState;
    } catch (error) {
      handleExportApiError(normalizedChannel, error, {
        fingerprint: normalizedFingerprint,
      });
      return exportStatesRef.current[normalizedChannel];
    }
  }, [clearChannelNotice, clearPendingDocumentWindow, dismissLoadingToast, handleExportApiError, openLoadingToast, preparePdfDocumentWindow, publishRunningNotice, setChannelState, showToast]);

  const downloadReadyExport = React.useCallback(async (channel, {
    documentWindow = null,
    exportId = "",
    fingerprint = "",
    openDocumentWindow = false,
    rowCount = null,
  } = {}) => {
    const normalizedChannel = normalizeChannel(channel);
    const currentState = exportStatesRef.current[normalizedChannel];
    if ((!exportId && currentState.status !== "ready_async") || !(exportId || currentState.exportId)) {
      return currentState;
    }

    return performPdfDownload(normalizedChannel, {
      documentWindow,
      exportId: exportId || currentState.exportId,
      fingerprint: fingerprint || currentState.fingerprint,
      openDocumentWindow,
      rowCount: rowCount ?? currentState.rowCount,
    });
  }, [performPdfDownload]);

  const syncExportFingerprint = React.useCallback((channel, fingerprint, {
    resultUrl = "",
  } = {}) => {
    const normalizedChannel = normalizeChannel(channel);
    const normalizedFingerprint = normalizeFingerprint(fingerprint);
    const resolvedResultUrl = resolveChannelReadyRoute(normalizedChannel, resultUrl);
    const currentState = exportStatesRef.current[normalizedChannel];
    if (!normalizedFingerprint) {
      if (currentState.status === "launching" || currentState.status === "pending_async") {
        return;
      }

      clearChannelNotice(normalizedChannel);
      dismissLoadingToast(normalizedChannel);
      runtimeRefs.current[normalizedChannel].pollAbortController?.abort();
      runtimeRefs.current[normalizedChannel].historyAbortController?.abort();
      runtimeRefs.current[normalizedChannel].hydratedFingerprint = "";
      clearPendingDocumentWindow(normalizedChannel);
      setChannelState(normalizedChannel, buildDefaultExportState());
      return;
    }

    if (currentState.status === "launching" || currentState.status === "pending_async") {
      return;
    }

    if (currentState.fingerprint && currentState.fingerprint !== normalizedFingerprint) {
      clearChannelNotice(normalizedChannel);
      dismissLoadingToast(normalizedChannel);
      runtimeRefs.current[normalizedChannel].hydratedFingerprint = "";
      setChannelState(normalizedChannel, buildDefaultExportState());
    }

    if (currentState.fingerprint === normalizedFingerprint && currentState.exportId) {
      if (currentState.resultUrl !== resolvedResultUrl) {
        setChannelState(normalizedChannel, {
          ...currentState,
          resultUrl: resolvedResultUrl,
        });
      }
      return;
    }

    void restoreFromHistory(normalizedChannel, normalizedFingerprint, { resultUrl: resolvedResultUrl });
  }, [clearChannelNotice, clearPendingDocumentWindow, dismissLoadingToast, restoreFromHistory, setChannelState]);

  const downloadReadyFleetTruckSheetPdfExport = React.useCallback(
    async () => downloadReadyExport("fleet"),
    [downloadReadyExport],
  );
  const launchFleetTruckSheetPdfExport = React.useCallback(
    async (options = {}) => launchTrackedExport({ ...options, channel: "fleet" }),
    [launchTrackedExport],
  );
  const syncFleetTruckSheetPdfFingerprint = React.useCallback(
    (fingerprint, options = {}) => syncExportFingerprint("fleet", fingerprint, options),
    [syncExportFingerprint],
  );
  const downloadReadySelectedTruckSheetPdfExport = React.useCallback(
    async () => downloadReadyExport("selected"),
    [downloadReadyExport],
  );
  const launchSelectedTruckSheetPdfExport = React.useCallback(
    async (options = {}) => launchTrackedExport({ ...options, channel: "selected" }),
    [launchTrackedExport],
  );
  const syncSelectedTruckSheetPdfFingerprint = React.useCallback(
    (fingerprint, options = {}) => syncExportFingerprint("selected", fingerprint, options),
    [syncExportFingerprint],
  );

  const contextValue = React.useMemo(() => ({
    fleet: {
      downloadReadyTruckSheetPdfExport: downloadReadyFleetTruckSheetPdfExport,
      exportState: exportStates.fleet,
      launchTruckSheetPdfExport: launchFleetTruckSheetPdfExport,
      syncTruckSheetPdfFingerprint: syncFleetTruckSheetPdfFingerprint,
    },
    selected: {
      downloadReadyTruckSheetPdfExport: downloadReadySelectedTruckSheetPdfExport,
      exportState: exportStates.selected,
      launchTruckSheetPdfExport: launchSelectedTruckSheetPdfExport,
      syncTruckSheetPdfFingerprint: syncSelectedTruckSheetPdfFingerprint,
    },
  }), [
    downloadReadyFleetTruckSheetPdfExport,
    downloadReadySelectedTruckSheetPdfExport,
    exportStates.fleet,
    exportStates.selected,
    launchFleetTruckSheetPdfExport,
    launchSelectedTruckSheetPdfExport,
    syncFleetTruckSheetPdfFingerprint,
    syncSelectedTruckSheetPdfFingerprint,
  ]);

  return (
    <TruckSheetPdfExportTrackerContext.Provider value={contextValue}>
      {children}
    </TruckSheetPdfExportTrackerContext.Provider>
  );
}

export function useTruckSheetPdfExportTracker(channel = "fleet") {
  const normalizedChannel = normalizeChannel(channel);
  const context = React.useContext(TruckSheetPdfExportTrackerContext);
  if (context?.[normalizedChannel]) {
    return context[normalizedChannel];
  }

  return {
    downloadReadyTruckSheetPdfExport: async () => buildDefaultExportState(),
    exportState: buildDefaultExportState(),
    launchTruckSheetPdfExport: async () => buildDefaultExportState(),
    syncTruckSheetPdfFingerprint() {},
  };
}
